import * as React from 'react';
import { RotateCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { PageHeader, type PageHeaderProps } from './page-header';

export type PageShellProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> &
  Pick<
    PageHeaderProps,
    | 'title'
    | 'subtitle'
    | 'meta'
    | 'info'
    | 'back'
    | 'actions'
    | 'primaryAction'
    | 'secondaryActions'
    | 'tertiaryActions'
  > & {
    compact?: boolean;
    fullHeight?: boolean;
    contentClassName?: string;
    children?: React.ReactNode;
  };

/** A page: its header, then its content, inside the page gutter. */
export function PageShell({
  title,
  subtitle,
  meta,
  info,
  back,
  compact = false,
  actions,
  primaryAction,
  secondaryActions,
  tertiaryActions,
  fullHeight = false,
  className,
  contentClassName,
  children,
  ...props
}: PageShellProps) {
  return (
    <div
      className={cn(
        'animate-fade-in px-[var(--space-page)] pb-[var(--space-page)]',
        compact ? 'pt-5' : 'pt-7',
        fullHeight && 'flex h-full flex-col',
        className
      )}
      {...props}
    >
      <PageHeader
        title={title}
        subtitle={subtitle}
        meta={meta}
        info={info}
        back={back}
        compact={compact}
        actions={actions}
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        tertiaryActions={tertiaryActions}
      />
      <div className={cn(fullHeight && 'min-h-0 flex-1', contentClassName)}>{children}</div>
    </div>
  );
}

export interface PageSkeletonProps {
  title?: string;
  showSubtitle?: boolean;
  showActions?: boolean;
  rows?: number;
}

/** The page's layout while it loads, without jumping when content arrives. */
export function PageSkeleton({
  title,
  showSubtitle = true,
  showActions = false,
  rows = 3,
}: PageSkeletonProps) {
  return (
    <div
      className="px-[var(--space-page)] pb-[var(--space-page)] pt-7"
      aria-busy="true"
      aria-label={title ? `Loading ${title}` : 'Loading'}
    >
      <div className="mb-6 flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          {title ? (
            <h1 className="text-page-title text-foreground">{title}</h1>
          ) : (
            <div className="h-8 w-40 animate-pulse rounded-md bg-muted" />
          )}
          {showSubtitle ? <div className="mt-2.5 h-4 w-64 max-w-full animate-pulse rounded-md bg-muted" /> : null}
        </div>
        {showActions ? <div className="h-9 w-32 animate-pulse rounded-md bg-muted" /> : null}
      </div>
      <div className="divide-y divide-border border-t border-border">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="py-4">
            <div className="mb-2.5 h-4 w-2/5 animate-pulse rounded-md bg-muted" />
            <div className="h-3.5 w-3/4 animate-pulse rounded-md bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

export interface PageErrorProps {
  message: string;
  onRetry?: () => void;
}

/** The page could not load: what happened, and a way to try again. */
export function PageError({ message, onRetry }: PageErrorProps) {
  return (
    <div className="px-[var(--space-page)] pb-[var(--space-page)] pt-7">
      <div
        role="alert"
        className="mx-auto flex max-w-lg flex-col items-center rounded-lg border border-border px-6 py-10 text-center"
      >
        <p className="text-sm font-medium text-foreground">{message}</p>
        {onRetry ? (
          <Button variant="secondary" className="mt-4" onClick={onRetry}>
            <RotateCw aria-hidden="true" />
            Try again
          </Button>
        ) : null}
      </div>
    </div>
  );
}
